import type { GeoBounds } from '@/services/dashboardService'
import type { MapBounds } from '@/services/consumerService'

export type LatLng = { lat: number; lng: number }

export type MapViewUnavailableReason =
  | 'no-site'
  | 'no-coordinates'
  | 'no-markers'
  | 'markers-error'
  | 'extent-error'

/** What the map should show, decided before any camera is handed to Google. */
export type MapView =
  | { kind: 'pending' }
  | { kind: 'bounds'; bounds: GeoBounds; center: LatLng }
  | { kind: 'point'; center: LatLng; zoom: number }
  | { kind: 'unavailable'; reason: MapViewUnavailableReason }

export type Fetched<T> =
  | { status: 'loading' }
  | { status: 'loaded'; value: T }
  | { status: 'failed' }

export type MapViewInput = {
  accountId: string | null
  showAllMarkers: boolean
  coordinates: { latitude: number; longitude: number } | null
  /** A loaded null is a site whose premises have no coordinates. */
  extent: Fetched<GeoBounds | null>
  /** Positions from the discovery fetch only, never from a viewport fetch. */
  markers: Fetched<LatLng[]>
}

export const SINGLE_POINT_ZOOM = 16
export const INITIAL_BOUNDS_ZOOM = 13
export const DISCOVERY_ZOOM = 8

/**
 * Marker box for the first fetch of a site whose extent is unknown. It covers
 * the whole globe on purpose: the alternative is a guess about which country
 * the tenant is in, and the Gateway clusters by zoom, not by box size.
 */
export const DISCOVERY_BOX: MapBounds = {
  xmin: -180,
  ymin: -90,
  xmax: 180,
  ymax: 90,
}

/**
 * An exact 0,0 is the Gulf of Guinea, never a water-utility premise: it is what
 * a missing coordinate looks like once it is read as a number.
 */
export const isUsableLatLng = (point: LatLng): boolean =>
  Number.isFinite(point.lat) &&
  Number.isFinite(point.lng) &&
  Math.abs(point.lat) <= 90 &&
  Math.abs(point.lng) <= 180 &&
  !(point.lat === 0 && point.lng === 0)

export const boundsOfPositions = (positions: LatLng[]): GeoBounds | null => {
  const usable = positions.filter(isUsableLatLng)
  if (usable.length === 0) return null

  return usable.reduce<GeoBounds>(
    (box, point) => ({
      minLat: Math.min(box.minLat, point.lat),
      minLng: Math.min(box.minLng, point.lng),
      maxLat: Math.max(box.maxLat, point.lat),
      maxLng: Math.max(box.maxLng, point.lng),
    }),
    {
      minLat: usable[0].lat,
      minLng: usable[0].lng,
      maxLat: usable[0].lat,
      maxLng: usable[0].lng,
    }
  )
}

export const extentToBox = (extent: GeoBounds): MapBounds => ({
  xmin: extent.minLng,
  ymin: extent.minLat,
  xmax: extent.maxLng,
  ymax: extent.maxLat,
})

export const firstFetchBox = (
  extent: GeoBounds | null
): { box: MapBounds; zoom: number } => ({
  box: extent ? extentToBox(extent) : DISCOVERY_BOX,
  zoom: DISCOVERY_ZOOM,
})

const viewOfBounds = (bounds: GeoBounds): MapView => {
  const center = {
    lat: (bounds.minLat + bounds.maxLat) / 2,
    lng: (bounds.minLng + bounds.maxLng) / 2,
  }

  // A box of one point has no zoom level to fit; aim at it directly.
  if (bounds.minLat === bounds.maxLat && bounds.minLng === bounds.maxLng) {
    return { kind: 'point', center, zoom: SINGLE_POINT_ZOOM }
  }

  return { kind: 'bounds', bounds, center }
}

/**
 * Decides where the map opens. It never falls back to a location: until the
 * site extent or the markers say where the data is, the view stays pending.
 */
export const decideMapView = (input: MapViewInput): MapView => {
  if (!input.showAllMarkers) {
    const point = input.coordinates && {
      lat: input.coordinates.latitude,
      lng: input.coordinates.longitude,
    }
    if (!point || !isUsableLatLng(point)) {
      return { kind: 'unavailable', reason: 'no-coordinates' }
    }
    return { kind: 'point', center: point, zoom: SINGLE_POINT_ZOOM }
  }

  const { extent, markers } = input
  if (!input.accountId) return { kind: 'unavailable', reason: 'no-site' }
  if (extent.status === 'loading') return { kind: 'pending' }
  // Checked before the extent: a map on the extent with no markers on it reads
  // as a site with no data.
  if (markers.status === 'failed') {
    return { kind: 'unavailable', reason: 'markers-error' }
  }

  // The markers win once they are in: they are where the data actually is, and
  // a filtered result is narrower than the extent of the whole site.
  const markerBounds =
    markers.status === 'loaded' ? boundsOfPositions(markers.value) : null
  if (markerBounds) return viewOfBounds(markerBounds)

  if (extent.status === 'loaded' && extent.value) {
    return viewOfBounds(extent.value)
  }
  if (markers.status === 'loading') return { kind: 'pending' }
  if (extent.status === 'failed') {
    return { kind: 'unavailable', reason: 'extent-error' }
  }

  return { kind: 'unavailable', reason: 'no-markers' }
}

export type MapCamera = {
  center: LatLng
  zoom: number
  fitBounds: GeoBounds | null
}

/** The camera to mount Google Maps with, or null while there is nothing to show. */
export const cameraFromView = (view: MapView): MapCamera | null => {
  if (view.kind === 'bounds') {
    return {
      center: view.center,
      zoom: INITIAL_BOUNDS_ZOOM,
      fitBounds: view.bounds,
    }
  }
  if (view.kind === 'point') {
    return { center: view.center, zoom: view.zoom, fitBounds: null }
  }
  return null
}
